import React, { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, useMe, Skeleton } from 'src/common';
import { ProcurementsDisplay, OverviewQp } from 'src/models/procurements/types';
import AccessControlItem, { Mode } from 'src/common/AccessControlItem';
import differenceBy from 'lodash/differenceBy';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';
import { IMember, AccessControlPolicy } from 'src/models/acl/types';
import WorkspaceBadge from 'src/common/WorkspaceBadge';
import { useSetOverviewQp } from 'src/models/procurements/hooks';
import { useEmployees, useActiveUsers } from 'src/models/users/hooks';
import { DEFAULT_WORKSPACE_COLORS, DEFAULT_WORKSPACE_SETTING } from 'src/models/workspace/types';
import { ColorPicker } from 'src/shared';
import { toMembersList, getPreparedMembersArray } from 'src/models/acl/helpers';
import { useCreateWorkspace } from 'src/models/workspace/hooks';
import { IUserName } from 'src/models/users/types';

export const CreateWorkspace: FC = () => {
  const { t } = useTranslation();
  const { data: meData } = useMe();
  const userId = meData?.email;

  const isWorkspaceAclFeature = useFeatureFlag(FeatureFlag.Workspace_ACL);

  const { data: employees, loading: employeeLoading } = useEmployees();
  const [color, setColor] = useState(DEFAULT_WORKSPACE_SETTING.color);
  const [title, setTitle] = useState(DEFAULT_WORKSPACE_SETTING.name);
  const [selectedOwners, setSelectedOwners] = useState([] as string[]);
  const [selectedSubscribers, setSelectedSubscribers] = useState([] as string[]);
  const [selectedPolicy, setSelectedPolicy] = useState(DEFAULT_WORKSPACE_SETTING.subscribersPolicy);
  const [selectedOwnersPolicy, setSelectedOwnersPolicy] = useState(DEFAULT_WORKSPACE_SETTING.ownersPolicy);

  const owner = employees?.createdBy;
  const employeesList = useActiveUsers();

  const employeesDict = useMemo(() => {
    const dict: { [key: string]: IUserName } = {};
    employeesList &&
      employeesList.forEach(emp => {
        dict[emp.email] = emp;
      });
    return dict;
  }, [employeesList]);

  const subscribers = useMemo((): IMember[] => {
    if (employees && owner) {
      const subscribersList = selectedSubscribers.map(emp => employeesDict[emp]);
      return toMembersList(subscribersList || [], owner);
    } else {
      return []; // we can not show subscribers while we do not know who is an owner
    }
  }, [employees, employeesDict, owner, selectedSubscribers]);

  const owners = useMemo((): IMember[] => {
    if (employees && owner && userId) {
      const setOfOwners = new Set([userId, owner.email, ...selectedOwners]);

      const ownersList = Array.from(setOfOwners)
        .map(emp => employeesDict[emp])
        .filter(Boolean);
      return toMembersList(ownersList || [], owner, userId);
    } else {
      return []; // we can not show subscribers while we do not know who is an owner
    }
  }, [employees, employeesDict, owner, selectedOwners, userId]);

  const updateWSOwnersList = useCallback((owners: string[]) => {
    setSelectedOwners(owners);
  }, []);

  const updateWSSubscribersList = useCallback((subscribers: string[]) => {
    setSelectedSubscribers(subscribers);
  }, []);

  const onMoveMember = useCallback(
    (email: string, isOwner?: boolean) => {
      const ownersIds = owners.map(({ user }) => user.email);
      const subscribersIds = subscribers.map(({ user }) => user.email);

      const ownersVal = getPreparedMembersArray(ownersIds, email, isOwner);
      const subscribersVal = getPreparedMembersArray(subscribersIds, email, !isOwner);
      setSelectedOwners(ownersVal);
      setSelectedSubscribers(subscribersVal);
    },
    [owners, subscribers]
  );

  const changeWSPolicy = useCallback((policy: AccessControlPolicy) => {
    setSelectedPolicy(policy);
  }, []);

  const changeWSOwnerPolicy = useCallback((policy: AccessControlPolicy) => {
    setSelectedOwnersPolicy(policy);
  }, []);

  // SHOW ONLY MEMBERS WHO ARE NOT OWNER OR SUBSCRIBER  YET
  const membersOptions = useMemo((): IMember[] => {
    const employeesList = //FIXME: rename
      employees && owner ? toMembersList([owner, ...employees.personel.filter(emp => emp.enabled)], owner) : [];

    return differenceBy(employeesList, [...owners, ...subscribers], 'user.email');
  }, [employees, owner, owners, subscribers]);

  const isLoading = employeeLoading;

  const [form] = Form.useForm();

  const setOverviewQp = useSetOverviewQp();
  const [createWorkspace] = useCreateWorkspace();

  const onCreateWorkspace = useCallback(() => {
    if (!title) {
      form.setFields([
        {
          name: 'title',
          errors: [t('BidSpaces.enterBidSpaceName')]
        }
      ]);
    } else {
      const ownersList = owners.map(({ user }) => user.email);
      createWorkspace(
        {
          name: title,
          color,
          owners: selectedOwnersPolicy === AccessControlPolicy.ALL ? null : ownersList,
          subscribers: selectedPolicy === AccessControlPolicy.ALL ? null : selectedSubscribers
        },
        selectedPolicy,
        id => {
          id &&
            setOverviewQp({
              [OverviewQp.WorkspaceId]: id
            });
        }
      );
    }
  }, [
    title,
    form,
    t,
    owners,
    createWorkspace,
    selectedOwnersPolicy,
    selectedPolicy,
    selectedSubscribers,
    color,
    setOverviewQp
  ]);

  const onChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const onCancel = useCallback(() => {
    setOverviewQp({
      [OverviewQp.Tab]: ProcurementsDisplay.ALL //TODO: why ALL-tab? probably should be prev ws or wss[0] ??
    });
  }, [setOverviewQp]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('BidSpaces.createNewBidSpace')}</h2>

      <Form initialValues={{ title: DEFAULT_WORKSPACE_SETTING.name }} form={form} onFinish={onCreateWorkspace}>
        <div className={styles.unit}>
          <Form.Item
            label={t('Common.name')}
            name="title"
            rules={[{ required: true, message: t('BidSpaces.bidSpaceNameIsRequired') }]}
            className={styles.formItem}
          >
            <Input onChange={onChangeHandler} name="title" type="text" maxLength={30} placeholder={t('Common.name')} />
          </Form.Item>

          <ColorPicker
            currentColor={color}
            onSubmit={setColor}
            onChange={setColor}
            paletteColors={DEFAULT_WORKSPACE_COLORS}
            placement={'bottomRight'}
          >
            <div>
              <WorkspaceBadge title={title || 'W'} color={color} className={styles.badge} />
            </div>
          </ColorPicker>

          {!isWorkspaceAclFeature && (
            <Button type={'primary'} onClick={onCreateWorkspace} disabled={!title}>
              {t('Common.save')}
            </Button>
          )}
        </div>
      </Form>

      {isWorkspaceAclFeature ? (
        isLoading ? (
          <Skeleton active={isLoading} />
        ) : (
          <div className={styles.form}>
            <AccessControlItem
              myUserId={userId}
              readOnly={false}
              mode={Mode.Owners}
              membersOptions={membersOptions}
              members={owners}
              onUpdate={updateWSOwnersList}
              onMove={onMoveMember}
              policy={selectedOwnersPolicy}
              onChangePolicy={changeWSOwnerPolicy}
              policyDescription={t('Common.ACL.setEverybodyAsEditor')}
              description={t('BidSpaces.infoAboutBSOwners')}
            />
            <AccessControlItem
              myUserId={userId}
              readOnly={false}
              mode={Mode.Subscribers}
              membersOptions={membersOptions}
              members={subscribers}
              onUpdate={updateWSSubscribersList}
              onMove={onMoveMember}
              policy={selectedPolicy}
              onChangePolicy={changeWSPolicy}
              policyDescription={t('BidSpaces.giveAllTeammatesPermissionToBidSpace')}
              description={t('BidSpaces.infoAboutBSSubscribers')}
            />
          </div>
        )
      ) : null}
      {isWorkspaceAclFeature && (
        <div className={styles.footer}>
          <Button type={'text'} onClick={onCancel}>
            {t('Common.cancel')}
          </Button>
          <Button type={'primary'} onClick={onCreateWorkspace} disabled={!title}>
            {t('Common.save')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreateWorkspace;
